import { checkAuthStateChange, saveAuthState } from './authStateChecker.js';
import {
  CHATBOT_CONVERSATION_HISTORY,
  CHATBOT_TOKEN_STRING,
  CHATBOT_WELCOME_NAVIGATION
} from './constants';
import { addSentryBreadcrumb } from './sentryConfig.js';
import {
  generateUuidChatbot,
  isUuidExpiredChatbot,
  removeUuidChatbot,
  updateSessionIndexChatbot
} from './uuid';
import { ensureUuidConsistency } from './uuidTabSync';

export function initializeChatbotSession(verboseChatbot) {
  const historyMode = import.meta.env.VITE_CONVERSATION_HISTORY_MODE || 'enabled';
  
  if (verboseChatbot) {
    console.log('📋 Conversation History Mode:', historyMode);
    if (historyMode === 'disabled') {
      console.log('🔄 Fresh UUID will be generated on each page load');
      console.log('🚫 History will not be loaded from server');
    }
  }
  
  let socketChatbotUuid;
  
  if (historyMode === 'disabled') {
    // Always generate fresh UUID, skip all checks
    socketChatbotUuid = generateUuidChatbot();
    
    if (verboseChatbot) {
      console.log('🔄 Generated fresh UUID (history disabled):', socketChatbotUuid);
    }
    
    // Return minimal auth state check for disabled mode
    return { 
      socketChatbotUuid, 
      authStateCheck: { 
        changed: false, 
        event: null, 
        wasAuthenticated: false, 
        currentlyAuthenticated: false 
      } 
    };
  }
  
  // Existing logic for enabled mode
  socketChatbotUuid = generateUuidChatbot();

  if (isUuidExpiredChatbot()) {
    if (verboseChatbot) {
      console.log('🕐 UUID expired, generating new session');
    }
    socketChatbotUuid = generateUuidChatbot();
  }

  socketChatbotUuid = ensureUuidConsistency(socketChatbotUuid);

  const authStateCheck = checkAuthStateChange();
  if (authStateCheck.changed) {
    if (verboseChatbot) {
      console.log('🔐 Auth state changed on page load:', {
        event: authStateCheck.event,
        wasAuthenticated: authStateCheck.wasAuthenticated,
        currentlyAuthenticated: authStateCheck.currentlyAuthenticated
      });
    }
    
    const resetSession = (eventType) => {
      if (verboseChatbot) {
        const message = eventType === 'logout' 
          ? '🚪 User logged out - Resetting session completely'
          : '🔓 User logged in - Creating new authenticated session';
        console.log(message);
      }
      
      const oldUuid = socketChatbotUuid;
      
      if (verboseChatbot) {
        console.log('🧹 Removing UUID cookies before generating new UUID');
      }
      removeUuidChatbot();
      localStorage.removeItem(CHATBOT_CONVERSATION_HISTORY);
      localStorage.removeItem(CHATBOT_WELCOME_NAVIGATION);
      
      if (verboseChatbot) {
        console.log('🧹 UUID cookies removed, now generating completely new UUID');
      }
      
      if (eventType === 'logout') {
        window.sessionStorage.removeItem(CHATBOT_TOKEN_STRING);
      }
      
      socketChatbotUuid = generateUuidChatbot();
      updateSessionIndexChatbot('0');
      
      if (oldUuid === socketChatbotUuid) {
        console.warn('⚠️ WARNING: Base UUID did NOT change! This is a problem.');
        
        const error = new Error('UUID did not change after auth state change');
        error.name = 'UUIDChangeFailure';
        
        addSentryBreadcrumb({
          category: 'auth',
          message: `UUID failed to change on ${eventType}`,
          level: 'error',
          data: {
            eventType,
            oldUuid,
            newUuid: socketChatbotUuid,
            oldBaseUuid: oldUuid,
            newBaseUuid: socketChatbotUuid,
            wasAuthenticated: authStateCheck.wasAuthenticated,
            currentlyAuthenticated: authStateCheck.currentlyAuthenticated
          }
        });
        
        if (window.Sentry) {
          window.Sentry.captureException(error, {
            tags: {
              feature: 'auth-state-security',
              component: 'uuid-generation',
              severity: 'high'
            },
            extra: {
              eventType,
              oldUuid,
              newUuid: socketChatbotUuid,
              oldBaseUuid: oldUuid,
              newBaseUuid: socketChatbotUuid,
              authStateChanged: authStateCheck.changed,
              wasAuthenticated: authStateCheck.wasAuthenticated,
              currentlyAuthenticated: authStateCheck.currentlyAuthenticated
            }
          });
        }
      }
    };

    if (authStateCheck.event === 'logout' || authStateCheck.event === 'login') {
      resetSession(authStateCheck.event);
    }
  }

  saveAuthState(authStateCheck.currentlyAuthenticated, socketChatbotUuid);

  if (verboseChatbot) {
    console.log('🔑 Chatbot Session UUID:', socketChatbotUuid);
    console.log('📝 Base UUID (shared across tabs):', socketChatbotUuid.replace(/ - \d+$/, ''));
    console.log('🏷️  Session Index:', socketChatbotUuid.split(' - ')[1] || '0');
    if (authStateCheck.changed) {
      console.log('🔄 Session reset due to auth change:', authStateCheck.event);
    }
  }

  return { socketChatbotUuid, authStateCheck };
}

